<template>
    <div>
        <rkm-row>
            <rkm-col>
                <rkm-data-table
                    :fields="fields"
                    :repository="repositories.paciente"
                    :request-params="requestParams"
                    :tbody-tr-class="rowClass"
                    :disabled-button-search="true"
                    class="tabela-paciente"
                    ref="DataTable"
                    sort-by="nome"
                >
                    <template v-slot:cell(nome)="{ item }">
                        {{ item.nome }}

                        <template v-if="item.nomeSocial">
                            <template v-if="item.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                                <strong>({{ item.nomeSocial }})</strong>
                            </template>

                            <template v-else>
                                ({{ item.nomeSocial }})
                            </template>
                        </template>

                        <span
                            v-if="item.obitoEm"
                            class="ml-1"
                            title="Paciente em óbito"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="cross" />
                        </span>

                        <span
                            v-if="item.bloqueado"
                            class="ml-1"
                            title="Cadastro bloqueado"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="ban" />
                        </span>

                        <span
                            v-if="isProvisorioOuTemporario(item)"
                            class="ml-1"
                            title="Paciente provisório/temporário"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="hourglass-half" />
                        </span>

                        <span
                            v-if="item.classificacaoId == enums.classificacao.OUTRO_MUNICIPIO"
                            class="ml-1"
                            title="Paciente não munícipe"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="map-pin" />
                        </span>

                        <span
                            v-if="!item.cpf && !item.cns"
                            class="ml-1"
                            title="Paciente sem CPF e CNS"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="exclamation-triangle" />
                        </span>

                        <span
                            v-if="item.pacienteResponsavelFamiliar"
                            class="ml-1"
                            title="Responsável familiar"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="crown" />
                        </span>

                        <span
                            v-if="isIdoso(item.dataNascimento)"
                            class="ml-1"
                            title="Idoso"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="blind" />
                        </span>

                        <span
                            v-if="hasTea(item)"
                            class="ml-1"
                            title="Pessoa com Transtorno do Espectro Autista - TEA"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="infinity" />
                        </span>

                        <span
                            v-if="hasPcd(item)"
                            class="ml-1"
                            title="Pessoa com Deficiência - PCD"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="wheelchair" />
                        </span>
                    </template>

                    <template v-slot:cell(dataNascimento)="row">
                        {{ row.value | formatDate }}
                    </template>

                    <template v-slot:cell(cpf)="row">
                        {{ row.value | formatCpf }}
                    </template>

                    <template v-slot:cell(cns)="row">
                        {{ row.value | formatCns }}
                    </template>

                    <template v-slot:cell(carteiraIdentidadeNumero)="row">
                        {{ row.value | formatRg }}
                    </template>

                    <template v-slot:cell(dnv)="row">
                        {{ row.value | formatDnv }}
                    </template>

                    <template v-slot:cell(telefoneNumero)="row">
                        {{ row.value | formatPhoneOrCellPhone }}
                    </template>

                    <template v-slot:cell(actions)="{ item }">

                        <rkm-button
                            v-if="!item.bloqueado && form.isPermissaoBloquear"
                            authorize="update-cadastro-paciente"
                            color="dark"
                            title="Bloquear paciente"
                            @click="bloquearPaciente(item)"
                            small
                        >
                            <rkm-icon icon="ban" />
                        </rkm-button>

                        <rkm-button
                            v-if="item.bloqueado && form.isPermissaoBloquear"
                            authorize="update-cadastro-paciente"
                            color="success"
                            title="Desbloquear paciente"
                            @click="desbloquearPaciente(item)"
                            small
                        >
                            <rkm-icon icon="check-circle" />
                        </rkm-button>

                        <rkm-button
                            @click.stop="print(item.id)"
                            color="info"
                            authorize="show-cadastro-paciente"
                            title="Imprimir"
                            small
                        >
                            <rkm-icon icon="print" />
                        </rkm-button>

                        <rkm-button
                            @click.stop="$router.push({ name: 'cadastro-paciente-show', params: { id: item.id } })"
                            color="info"
                            authorize="show-cadastro-paciente"
                            title="Visualizar"
                            small
                        >
                            <rkm-icon icon="eye" />
                        </rkm-button>

                        <rkm-button
                            :disabled="handleEdit(item)"
                            @click.stop="$router.push({ name: 'cadastro-paciente-edit', params: { id: item.id } })"
                            color="primary"
                            authorize="update-cadastro-paciente"
                            title="Editar"
                            small
                        >
                            <rkm-icon icon="pen" />
                        </rkm-button>

                        <rkm-button
                            @click.stop="handleDestroy(item)"
                            color="danger"
                            authorize="destroy-cadastro-paciente"
                            title="Excluir"
                            small
                        >
                            <rkm-icon icon="trash" />
                        </rkm-button>
                    </template>
                </rkm-data-table>
            </rkm-col>
        </rkm-row>

        <rkm-row class="mt-3">
            <rkm-col>
                <p class="m-0">
                    <span class="legend-color purple" /> Paciente em óbito<br>
                    <span class="legend-color danger" /> Cadastro bloqueado<br>
                    <span class="legend-color orange" /> Paciente provisório ou temporário<br>
                    <span class="legend-color brown" /> Paciente não munícipe<br>
                    <span class="legend-color warning" /> Paciente sem CPF e CNS
                </p>
            </rkm-col>
        </rkm-row>

        <ModalImpressao ref="ModalImpressao" />
    </div>
</template>

<script>
// Components.
import ModalImpressao from './components/impressao-list/Modal'

// Mixins.
import { repository, error } from '@/mixins'

// Filters.
import formats from '@/filters/formats'

// Enums.
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

// Others.
import moment from 'moment'

export default {
    computed: {
        requestParams() {

            return { with_paciente_bloqueado: true,  with_paciente_provisorio: true }
        },
    },
    components: { ModalImpressao },
    created() {

        this.repositories.paciente = this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente')
    },
    data: () => ({
        fields: [
            {
                name: 'cadastroCidadao.nome',
                key: 'nome',
                label: 'Nome (Nome social)',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroCidadao.nomeSocial',
                key: 'nomeSocial',
                label: 'Nome social',
                searchable: true,
                sortable: true,
                visible: false
            },
            {
                name: 'cadastroCidadao.dataNascimento',
                key: 'dataNascimento',
                label: 'Data de nascimento',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroCidadao.nomeMae',
                key: 'nomeMae',
                label: 'Nome da mãe',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroPaciente.prontuario',
                key: 'prontuario',
                label: 'Prontuário municipal',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroCidadao.cpf',
                key: 'cpf',
                label: 'CPF',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroCidadao.cns',
                key: 'cns',
                label: 'CNS',
                searchable: true,
                sortable: true
            },
            {
                name: 'carteiraIdentidadeNumero',
                key: 'carteiraIdentidadeNumero',
                label: 'RG',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroCidadao.dnv',
                key: 'dnv',
                label: 'DNV',
                searchable: true,
                sortable: true
            },
            {
                name: 'telefoneNumero',
                key: 'telefoneNumero',
                label: 'Telefone',
                searchable: true,
                sortable: true
            },
            {
                name: 'dataHoraUltimaAtualizacao',
                key: 'dataHoraUltimaAtualizacao',
                label: 'Última atualização',
                searchable: true,
                sortable: true
            },
            {
                name: 'actions',
                key: 'actions',
                label: 'Ações',
                class: 'col-min'
            },
        ],
        repositories: {
            paciente: null,
        },
        enums: { preferenciaNome, classificacao, subclassificacao, deficienciaBase, deficiencia }
    }),
    filters: {
        formatDate: formats.formatDate,
        formatCns: formats.formatCns,
        formatCpf: formats.formatCpf,
        formatRg: formats.formatRg,
        formatPhoneOrCellPhone: formats.formatPhoneOrCellPhone,
        formatDnv: formats.formatDnv,
    },
    methods: {
        handleDestroy({ id }) {

            const destroy = () => this.repositories.paciente.destroy(id)
                .then(() => this.$refs.DataTable.refresh())

            this.showDeleteConfirmation('Deseja realmente excluir o paciente?', destroy, this.handleError)
        },
        isProvisorioOuTemporario(item) {

            return item.classificacaoId == this.enums.classificacao.PROVISORIO
                || item.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO
        },
        isIdoso(dataNascimento) {

            return !!dataNascimento && moment().diff(moment(dataNascimento), 'years') >= 60
        },
        getDeficienciasBaseIds(item) {

            return item.deficienciasBaseIds
                ? item.deficienciasBaseIds.split(',').map(Number)
                : []
        },
        hasTea(item) {

            return this.getDeficienciasBaseIds(item).includes(this.enums.deficienciaBase.TEA)
                || item.deficienciaId === this.enums.deficiencia.AUTISMO
        },
        hasPcd(item) {

            return this.getDeficienciasBaseIds(item).some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!item.deficienciaId && item.deficienciaId !== this.enums.deficiencia.AUTISMO)
        },
        rowClass(item, type) {
            if(item && type === 'row') {
                if (item.obitoEm) {
                    return 'table-purple'
                }
                if (item.bloqueado) {
                    return 'table-danger'
                }
                if (this.isProvisorioOuTemporario(item)) {
                    return 'table-orange'
                }
                if (item.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {
                    return 'table-brown'
                }
                if (!item.cpf && !item.cns) {
                    return 'table-warning'
                }
            }
        },
        bloquearPaciente(item) {

            const bloquear = () => this.repositories.paciente.bloquear(item.id)
                .then(() => this.$refs.DataTable.refresh())

            this.createNotification({
                type: 'dialog',
                color: 'danger',
                title: 'Confirmação de bloqueio',
                message: `Deseja bloquear o paciente ${item.nome}?`,
                confirm: bloquear,
                onError: this.handleError,
            })
        },
        desbloquearPaciente(item) {

            const desbloquear = () => this.repositories.paciente.desbloquear(item.id)
                .then(() => this.$refs.DataTable.refresh())

            this.createNotification({
                type: 'dialog',
                color: 'success',
                title: 'Confirmação de desbloqueio',
                message: `Deseja desbloquear o paciente ${item.nome}?`,
                confirm: desbloquear,
                onError: this.handleError,
            })
        },
        handleEdit(item) {

            if (!this.form.isPermissaoCadastro) {
                return true;
            }

            if (this.form.isNotParametrizado) {
                return false;
            }

            const classificacoes = this.form.classificacoes;
            const classificacaoId = item.classificacaoId;

            const isClassificacaoPresent = (classificacaoId) => {
                return classificacoes.some(classificacao => classificacao.id === classificacaoId);
            };

            switch (classificacaoId) {
                case this.enums.classificacao.MUNICIPE:
                case this.enums.classificacao.PROVISORIO:
                case this.enums.classificacao.OUTRO_MUNICIPIO:
                    return !isClassificacaoPresent(classificacaoId);
                default:
                    return false;
            }
        },
        print(id) {
            this.$refs.ModalImpressao.open(id)
        },
    },
    mixins: [ repository, error ],
    props: {
        form: { type: Object, required: true },
    },
}
</script>

<style scoped>
.legend-color {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    margin-right: 4px;
}
.legend-color.purple {
    background-color: #6f42c1;
}
.legend-color.danger {
    background-color: var(--danger);
}
.legend-color.orange {
    background-color: #F28C28;
}
.legend-color.brown {
    background-color: #6F4E37;
}
.legend-color.warning {
    background-color: var(--warning);
}

.tabela-paciente >>> .table-orange {
    border-color: #F28C28;
    background-color: #FFE0B2;
}

.tabela-paciente >>> .table-brown {
    border-color: #6F4E37;
    background-color: #D2B48C;
}

.tabela-paciente >>> .table-purple {
    border-color: #6f42c1;
    background-color: #E1D4F2;
}
</style>